import { GroupResult } from './classes/GroupResult'
import { Aggregator, Dictionary, IgnoredTreeKeys, TreeRoot } from './types'

export class TreeBuilder {
  private static _maxLeafValue: number = 0

  static set maxLeafValue(value: number) {
    if (value > this._maxLeafValue) {
      this._maxLeafValue = value
    }
  }

  static get maxLeafValue() {
    return this._maxLeafValue
  }

  static group<T extends any, K extends keyof T>(
    arr: T[],
    keys: Array<K>,
    size: number,
    filterKeys?: Map<K, Set<string>>,
    aggregator?: Aggregator,
    aggregatorKey?: keyof T
  ): Dictionary<T> & TreeRoot {
    let result: Dictionary<T> & TreeRoot
    if (aggregator?.chain) {
      for (let agg of aggregator.chain) {
        result = this.groupRecursor(arr, keys, result?.nodeValue || size, filterKeys, agg, aggregatorKey)
      }
    }
    this._maxLeafValue = 0
    return this.groupRecursor(arr, keys, result?.nodeValue || size, filterKeys, aggregator, aggregatorKey)
  }

  private static groupRecursor<T extends any, K extends keyof T>(
    arr: T[],
    keys: Array<K>,
    size: number,
    filterKeys?: Map<K, Set<string>>,
    aggregator?: Aggregator,
    aggregatorKey?: keyof T
  ): any {
    let key = keys[0]

    if (!key) {
      let result: GroupResult
      if (aggregator?.value && aggregator.keyDependent && aggregatorKey) {
        result = new GroupResult(aggregator.value(arr.map((v) => v[aggregatorKey]) as any))
      } else if (aggregator?.value) {
        result = new GroupResult(aggregator.value([arr.length], size))
      } else {
        result = new GroupResult(arr.length)
      }
      this.maxLeafValue = result.nodeValue
      return result
    }

    const obj: Dictionary<T> & TreeRoot = groupByKey(arr, key, filterKeys)

    obj.nodeKey = key as string

    let count: number[] = []
    Object.keys(obj)
      .filter((k) => !IgnoredTreeKeys.includes(k))
      .forEach((k) => {
        const arr = obj[k]
        const groupResult = this.groupRecursor(
          arr,
          keys.slice(1, keys.length),
          size,
          filterKeys,
          aggregator,
          aggregatorKey
        )
        if (
          !(groupResult instanceof GroupResult) &&
          Object.keys(groupResult).filter((k) => !IgnoredTreeKeys.includes(k)).length === 0
        ) {
          delete obj[k]
        } else {
          obj[k] = groupResult
          count.push(obj[k].nodeValue)
        }
      })

    if (count.length > 0) {
      if (aggregator?.value && aggregator.keyDependent && aggregatorKey) {
        obj.nodeValue = aggregator.value(count)
      } else {
        obj.nodeValue = count.reduce((prev, curr) => prev + curr)
      }
    }
    return obj
  }
}

function groupByKey<T extends any, K extends keyof T>(arr: T[], key: K, filterKeys?: Map<K, Set<string>>) {
  return arr.reduce((result, curr) => {
    const keyValue = curr[key] as any

    for (let [filterKey, filterValues] of filterKeys) {
      const objValue = curr[filterKey]

      if (!filterValues.has(objValue as any)) {
        return result
      }
    }

    if (!result[keyValue]) {
      result[keyValue] = []
    }

    result[keyValue].push(curr)

    return result
  }, {} as Dictionary<T>)
}
